import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'

import { GRAY_200 } from 'constants/styling/theme'
import { MAX_WORKSPACE_NAME_LENGTH } from 'constants/workspaces'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import Modal from 'components/common/Modals/Modal/Modal'
import { Stack } from '@mui/material'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUpdateWorkspaceName } from 'dataQueries/workspace.query'
import { useQueryClient } from '@tanstack/react-query'
import { EntityKeys } from 'utils/reactQuery/queryTypes'

interface EditWorkspaceNameModalProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  currentName: string
}

export const EditWorkspaceNameModal: FC<EditWorkspaceNameModalProps> = ({ isOpen, onClose, workspaceId, currentName }) => {
  const { t } = useTranslation(['workspace_settings', 'actions'])
  const queryClient = useQueryClient()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const mutation = useUpdateWorkspaceName()

  const [workspaceName, setWorkspaceName] = useState(currentName)

  useEffect(() => {
    setWorkspaceName(currentName)
  }, [currentName])

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setWorkspaceName(e.target.value)
  }, [])

  const handleClose = useCallback(() => {
    setWorkspaceName(currentName)
    onClose()
  }, [currentName, onClose])

  const handleSave = useCallback(async () => {
    try {
      await mutation.mutateAsync({ workspaceId, title: workspaceName.trim() })
      spawnSuccessToast(t('workspace_settings:information.update_success'))
      queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
      onClose()
    } catch {
      spawnErrorToast(t('workspace_settings:information.update_error'))
    }
  }, [queryClient, mutation, onClose, spawnErrorToast, spawnSuccessToast, t, workspaceId, workspaceName])

  const isSaveDisabled = !workspaceName.trim() || workspaceName.trim() === currentName

  return (
    <Modal
      isOpen={isOpen}
      title={t('workspace_settings:information.new_workspace_name')}
      onClickOutside={handleClose}
      onClose={handleClose}
      contentStyle={{ width: '39rem' }}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap="0.8rem" padding="0.8rem 0" borderTop={`1px solid ${GRAY_200}`}>
          <MUIButton type="secondaryBorder" onClick={handleClose}>
            {t('actions:Cancel')}
          </MUIButton>
          <MUIButton type="defaultPrimary" onClick={handleSave} isLoading={mutation.isPending} disabled={isSaveDisabled}>
            {t('workspace_settings:information.save_changes')}
          </MUIButton>
        </Stack>
      }
    >
      <Stack padding="2.4rem 0 3.2rem 0" width="100%" boxSizing="border-box">
        <MUIInputField label={t('workspace_settings:information.workspace_name')} value={workspaceName} onChange={handleChange} maxLength={MAX_WORKSPACE_NAME_LENGTH} />
      </Stack>
    </Modal>
  )
}
